import { useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Plus, Search, Edit2, Trash2, Eye, Copy, Package, ChevronLeft, ChevronRight, ChevronDown, Check, Download, ArrowUp, ArrowDown, ArrowUpDown, ClipboardList } from 'lucide-react';
import { BsFileSpreadsheet, BsBox } from 'react-icons/bs';
import * as XLSX from 'xlsx';
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { productAPI, authAPI } from '../../api';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import './Product.css';
import AccessModule from '../../components/AccessModule/AccessModule';

export default function ProductsPage() {
  const navigate = useNavigate();
  const { products, setProducts, loading, pagination, fetchProducts } = useOutletContext<any>();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [sortBy, setSortBy] = useState('_id');
  const [sortOrder, setSortOrder] = useState('desc');
  const [deleting, setDeleting] = useState<any>(null);
  const [copiedSku, setCopiedSku] = useState<string | null>(null);
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const userDetails = JSON.parse(localStorage.getItem('seller') || '{}');

  const [accessChecked, setAccessChecked] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState('');
  const [isBlocked, setIsBlocked] = useState(false);

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const userDetails = JSON.parse(
          localStorage.getItem("seller") || "{}"
        );

        const sellerRes = await authAPI.getSellerStatus(
          userDetails._id
        );

        setVerificationStatus(
          sellerRes.data.verification_status
        );

        setIsBlocked(
          sellerRes.data.isBlock
        );

        setAccessChecked(true);
      } catch (err) {
        console.error(err);
        setAccessChecked(true);
      }
    };

    checkAccess();
  }, []);

  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    if (!accessChecked) return;

    if (
      isBlocked ||
      verificationStatus === "P" ||
      verificationStatus === "R"
    ) {
      return;
    }

    fetchProducts({
      search: debouncedSearch,
      status,
      seller_id: userDetails._id,
      sortBy,
      sortOrder
    });
  }, [
    debouncedSearch,
    status,
    sortBy,
    sortOrder,
    accessChecked,
    isBlocked,
    verificationStatus
  ]);

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const handleDelete = async (id: any) => {
    const result = await Swal.fire({
      title: 'Are you sure!',
      text: 'Delete this product?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;

    setDeleting(id);
    try {
      const data = {
        product_id: id,
        seller_id: userDetails._id,
        status: 'R'
      };

      await productAPI.deleteProduct(data);
      toast.success('Product status changed to R');
      fetchProducts({ search, status, sortBy, sortOrder });
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to change status');
    } finally { setDeleting(null); }
  };

  const handleCopy = (sku: string) => {
    navigator.clipboard.writeText(sku);
    toast.success('SKU copied to clipboard');
    setCopiedSku(sku);
    setTimeout(() => setCopiedSku(null), 2000);
  };

  const getPaginationGroup = () => {
    const pages: (number | string)[] = [];
    const maxPages = Number(pagination?.pages) || 1;
    const currentPage = Number(pagination?.page) || 1;

    if (maxPages <= 8) {
      for (let i = 1; i <= maxPages; i++) pages.push(i);
    } else {
      const lb = 2, rb = 2;
      let sp = Math.max(lb + 1, currentPage - 1);
      let ep = Math.min(maxPages - rb, currentPage + 1);

      if (currentPage <= lb + 2) ep = lb + 3;
      if (currentPage >= maxPages - rb - 1) sp = maxPages - rb - 2;

      for (let i = 1; i <= lb; i++) pages.push(i);
      if (sp > lb + 1) pages.push("...");
      for (let i = sp; i <= ep; i++) pages.push(i);
      if (ep < maxPages - rb) pages.push("...");
      for (let i = maxPages - rb + 1; i <= maxPages; i++) pages.push(i);
    }
    return pages;
  };

  const [exporting, setExporting] = useState(false);

  const fetchExportData = async () => {
    const res = await productAPI.getAll({
      search,
      status,
      seller_id: userDetails._id,
      limit: 10000,
      page: 1
    });
    if (res.data.response === 'S') {
      return res.data.data || [];
    }
    return [];
  };

  const exportToPDF = async () => {
    try {
      setExporting(true);
      const dataToExport = await fetchExportData();

      if (dataToExport.length === 0) {
        Swal.fire({ title: 'No Data', text: 'There is no data to export.', icon: 'info', confirmButtonColor: '#14b8a6' });
        return;
      }

      const doc = new jsPDF();

      doc.setFont("Helvetica", "bold");
      doc.setFontSize(20);
      doc.setTextColor(20, 184, 166);
      doc.text("DECSOPLUS STORE", 14, 15);

      doc.setFontSize(10);
      doc.setFont("Helvetica", "normal");
      doc.setTextColor(100, 116, 139);
      doc.text("Products Report", 14, 21);

      const dateStr = new Date().toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
      doc.text(`Generated: ${dateStr}`, 196, 15, { align: 'right' });

      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.5);
      doc.line(14, 25, 196, 25);

      doc.setFontSize(9);
      doc.setFont("Helvetica", "bold");
      doc.setTextColor(15, 23, 42);
      doc.text(`Total Products: ${dataToExport.length}`, 14, 31);

      const tableHeaders = [["S.No", "Product Name", "SKU", "Price", "Discount", "Stock", "Status"]];
      const tableRows = dataToExport.map((p: any, index: number) => [
        (index + 1).toString(),
        p.name || '—',
        p.sku || '—',
        `Rs. ${Number(p.price || 0).toLocaleString()}`,
        `Rs. ${Number(p.discountPrice || 0).toLocaleString()}`,
        p.stock?.toString() || '0',
        p.status === 'A' ? 'Active' : p.status === 'D' ? 'Draft' : p.status === 'O' ? 'Out of Stock' : (p.status || '—')
      ]);

      autoTable(doc, {
        startY: 35,
        head: tableHeaders,
        body: tableRows,
        theme: 'striped',
        headStyles: { fillColor: [20, 184, 166], textColor: 255, fontSize: 9, fontStyle: 'bold', halign: 'left' },
        bodyStyles: { fontSize: 8, textColor: [51, 65, 85], valign: 'middle' },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        didDrawPage: (data: any) => {
          const pageCount = doc.getNumberOfPages();
          doc.setFontSize(8);
          doc.setFont("Helvetica", "normal");
          doc.setTextColor(148, 163, 184);
          doc.text(`Page ${data.pageNumber} of ${pageCount}`, data.settings.margin.left, doc.internal.pageSize.height - 10);
          doc.text("DecsoPlus © All Rights Reserved", doc.internal.pageSize.width - data.settings.margin.right, doc.internal.pageSize.height - 10, { align: 'right' });
        },
        margin: { top: 35, bottom: 15, left: 14, right: 14 }
      });

      doc.save(`DecsoPlus_Products_${new Date().toISOString().slice(0, 10)}.pdf`);
      Swal.fire({ title: 'Success!', text: 'Products exported to PDF successfully.', icon: 'success', confirmButtonColor: '#14b8a6' });
    } catch (err: any) {
      Swal.fire({ title: 'Export Failed', text: err.message || 'Something went wrong.', icon: 'error', confirmButtonColor: '#14b8a6' });
    } finally {
      setExporting(false);
    }
  };

  const exportToCSV = async () => {
    try {
      setExporting(true);
      const dataToExport = await fetchExportData();

      if (dataToExport.length === 0) {
        Swal.fire({ title: 'No Data', text: 'There is no data to export.', icon: 'info', confirmButtonColor: '#14b8a6' });
        return;
      }

      const headers = ["S.No", "Product Name", "SKU", "Price", "Discount Price", "Stock", "Status"];
      const rows = dataToExport.map((p: any, index: number) => [
        index + 1,
        `"${(p.name || '').replace(/"/g, '""')}"`,
        `"${(p.sku || '').replace(/"/g, '""')}"`,
        Number(p.price || 0),
        Number(p.discountPrice || 0),
        p.stock || 0,
        `"${(p.status === 'A' ? 'Active' : p.status === 'D' ? 'Draft' : p.status === 'O' ? 'Out of Stock' : (p.status || '')).replace(/"/g, '""')}"`
      ]);

      const csvContent = [headers.join(","), ...rows.map((r: any) => r.join(","))].join("\n");
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `DecsoPlus_Products_${new Date().toISOString().slice(0, 10)}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      Swal.fire({ title: 'Success!', text: 'Products exported to CSV successfully.', icon: 'success', confirmButtonColor: '#14b8a6' });
    } catch (err: any) {
      Swal.fire({ title: 'Export Failed', text: err.message || 'Something went wrong.', icon: 'error', confirmButtonColor: '#14b8a6' });
    } finally {
      setExporting(false);
    }
  };

  const handleExportClick = () => {
    Swal.fire({
      title: 'Export Format',
      text: 'Choose which format you want to export to:',
      icon: 'question',
      showCloseButton: true,
      showCancelButton: false,
      showDenyButton: true,
      confirmButtonText: 'Export as CSV',
      denyButtonText: 'Export as PDF',
      confirmButtonColor: '#10b981',
      denyButtonColor: '#f97316',
      width: '400px',
      customClass: {
        actions: 'my-actions',
        confirmButton: 'order-1',
        denyButton: 'order-2',
      }
    }).then((result) => {
      if (result.isConfirmed) {
        exportToCSV();
      } else if (result.isDenied) {
        exportToPDF();
      }
    });
  };

 

  if (
    isBlocked ||
    verificationStatus === "P" ||
    verificationStatus === "R"
  ) {
    return (
      <AccessModule
        isBlock={isBlocked}
        verification_status={verificationStatus}
      />
    );
  }


  return (
    <div className="fade-in">
      <div className="page-header" style={{ marginBottom: '20px' }}>
        <div>
          <h1 className="page-title">Products</h1>
          <p className="page-subtitle">View and manage your product collections</p>
        </div>
      </div>

      <div className="products-card">
        {/* Card Header: Search, Filters, Actions */}
        <div className="products-card-header">
          <div style={{ display: 'flex', gap: '12px', flex: 1, minWidth: '300px', flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', flex: 1, maxWidth: '350px', minWidth: '220px' }}>
              <Search size={14} color="#94a3b8" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
              <input
                className="form-input"
                placeholder="Search products, SKUs..."
                value={search}
                onChange={(e) => setSearch(e.target.value.trimStart())}
                style={{ background: 'white', borderRadius: '9999px', border: '1px solid #e2e8f0', padding: '8px 10px 8px 32px', fontSize: '13px' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
              {['all', 'active', 'draft', 'out_of_stock'].map((s) => {
                const isActive = status === s;
                let color = '';
                let label = '';
                if (s === 'all') { color = '#6E6BFA'; label = 'All Status'; }
                else if (s === 'active') { color = '#10b981'; label = 'Active'; }
                else if (s === 'draft') { color = '#3b82f6'; label = 'Draft'; }
                else if (s === 'out_of_stock') { color = '#f59e0b'; label = 'Out of Stock'; }

                return (
                  <button
                    key={s}
                    onClick={() => setStatus(s)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 12px',
                      borderRadius: '9999px',
                      border: `1px solid ${color}`,
                      backgroundColor: isActive ? color : 'white',
                      color: isActive ? 'white' : color,
                      fontWeight: '600',
                      fontSize: '13px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                  >
                    <span
                      style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        backgroundColor: isActive ? 'white' : color,
                      }}
                    />
                    {label}
                  </button>
                );
              })}
            </div>

            {search && (
              <button
                onClick={() => setSearch("")}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: '6px 12px', background: '#f8fafc', border: '1px solid #e2e8f0',
                  borderRadius: '9999px', cursor: 'pointer', transition: 'all 0.2s',
                  color: '#64748b', fontWeight: '600', fontSize: '12px'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#334155'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.color = '#64748b'; }}
              >
                Clear Search
              </button>
            )}
          </div>

          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              onClick={handleExportClick}
              disabled={exporting}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                background: 'white', border: '1px solid #e2e8f0', color: '#475569',
                borderRadius: '10px', padding: '11px 16px', fontWeight: '600', cursor: exporting ? 'not-allowed' : 'pointer', transition: 'all 0.2s', boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
              }}
              onMouseEnter={(e) => { if (!exporting) { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#cbd5e1'; } }}
              onMouseLeave={(e) => { if (!exporting) { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = '#e2e8f0'; } }}
            >
              <Download size={16} />
              {exporting ? 'Exporting...' : 'Export'}
            </button>
            <button
              className="btn-secondary"
              onClick={() => navigate('/tickets/create', { state: { category: 'Add Product', productRequest: true } })}
              title="Request a new product to be added to the catalog"
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                background: '#f0fdfa', border: '1px solid #14b8a6', color: '#0d9488',
                borderRadius: '10px', padding: '11px 16px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#ccfbf1'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#f0fdfa'; }}
            >
              <ClipboardList size={18} /> New Product Request
            </button>
            <button className="btn-primary" onClick={() => navigate('/products/add')} style={{ borderRadius: '10px', padding: '11px 20px', whiteSpace: 'nowrap' }}>
              <Plus size={18} /> Add Product
            </button>
          </div>
        </div>

        {/* Scrollable Table Body Container */}
        <div className="products-table-container">
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', gap: '16px' }}>
              <div style={{ width: '36px', height: '36px', border: '3px solid #f1f5f9', borderTop: '3px solid #3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
              <span style={{ fontSize: '15px', fontWeight: '500', color: '#475569' }}>Fetching your products...</span>
            </div>
          ) : products.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', width: '100%', background: '#fff', borderRadius: '12px' }}>
              <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: '16px' }}>
                <path d="M53.3333 18.6667H29.3333L24 13.3333H10.6667C7.72 13.3333 5.33333 15.72 5.33333 18.6667V45.3333C5.33333 48.28 7.72 50.6667 10.6667 50.6667H53.3333C56.28 50.6667 58.6667 48.28 58.6667 45.3333V24C58.6667 21.0533 56.28 18.6667 53.3333 18.6667Z" fill="url(#products-folder-grad)"/>
                <defs>
                  <linearGradient id="products-folder-grad" x1="5.33333" y1="13.3333" x2="58.6667" y2="50.6667" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#FCD34D"/>
                    <stop offset="1" stopColor="#F59E0B"/>
                  </linearGradient>
                </defs>
              </svg>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b', marginBottom: '8px', fontFamily: 'inherit' }}>
                No products found
              </h3>
              <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '24px', maxWidth: '400px', textAlign: 'center' }}>
                No records match the selected category, dates, or search term.
              </p>
              {(search || status !== 'all') ? (
                <button
                  onClick={() => { setSearch(''); setStatus('all'); }}
                  style={{ background: '#2563eb', color: '#ffffff', border: 'none', padding: '10px 24px', borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', transition: 'background 0.2s', boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)' }}
                  onMouseOver={(e) => e.currentTarget.style.background = '#1d4ed8'}
                  onMouseOut={(e) => e.currentTarget.style.background = '#2563eb'}
                >
                  Clear All Filters
                </button>
              ) : (
                <button className="btn-primary" onClick={() => navigate('/products/add')} style={{ borderRadius: '10px' }}>
                  <Plus size={18} /> Add First Product
                </button>
              )}
            </div>
          ) : (
            <table style={{ margin: 0 }}>
              <thead>
                <tr>
                  <th style={{ paddingLeft: '24px', width: '60px' }}>Sl.No</th>
                  <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      Product
                      {sortBy === 'name' ? (sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />) : <ArrowUpDown size={14} color="#94a3b8" opacity={0.5} />}
                    </div>
                  </th>
                  <th onClick={() => handleSort('sku')} style={{ cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      SKU
                      {sortBy === 'sku' ? (sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />) : <ArrowUpDown size={14} color="#94a3b8" opacity={0.5} />}
                    </div>
                  </th>
                  <th onClick={() => handleSort('mrp')} style={{ cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      MRP
                      {sortBy === 'mrp' ? (sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />) : <ArrowUpDown size={14} color="#94a3b8" opacity={0.5} />}
                    </div>
                  </th>
                  <th onClick={() => handleSort('price')} style={{ cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      Selling Price
                      {sortBy === 'price' ? (sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />) : <ArrowUpDown size={14} color="#94a3b8" opacity={0.5} />}
                    </div>
                  </th>
                  <th>Discount</th>
                  <th onClick={() => handleSort('stock')} style={{ cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      Stock
                      {sortBy === 'stock' ? (sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />) : <ArrowUpDown size={14} color="#94a3b8" opacity={0.5} />}
                    </div>
                  </th>
                  <th onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      Status
                      {sortBy === 'status' ? (sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />) : <ArrowUpDown size={14} color="#94a3b8" opacity={0.5} />}
                    </div>
                  </th>
                  <th style={{ paddingRight: '24px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p: any, index: number) => (
                  <tr key={p._id}>
                    <td style={{ paddingLeft: '24px', color: '#64748b', fontWeight: '600', fontSize: '14px' }}>
                      {(pagination?.page ? (pagination.page - 1) * (pagination.limit || 10) : 0) + index + 1}
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                        {p.images && p.images.length > 0 && (p.images[0]?.url || typeof p.images[0] === 'string') ? (
                          <img src={p.images[0]?.url || p.images[0]} alt={p.name} style={{ width: '44px', height: '44px', borderRadius: '10px', objectFit: 'cover', border: '1px solid #e2e8f0' }} />
                        ) : (
                          <div style={{ width: '44px', height: '44px', borderRadius: '10px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e2e8f0' }}>
                            <BsBox size={22} color="#94a3b8" />
                          </div>
                        )}
                        <div>
                          <div style={{ fontWeight: '700', color: '#0f172a', fontSize: '14px', maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                          <div style={{ fontSize: '13px', color: '#64748b', marginTop: '2px', fontWeight: '500' }}>{p.category}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontFamily: 'monospace', background: '#f1f5f9', color: '#475569', padding: '4px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', letterSpacing: '0.5px' }}>{p.sku}</span>
                        <button onClick={() => handleCopy(p.sku)} style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '4px', display: 'flex', borderRadius: '4px', transition: 'background 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.background = '#e2e8f0'} onMouseLeave={(e) => e.currentTarget.style.background = 'none'} title="Copy SKU">
                          {copiedSku === p.sku ? <Check size={14} color="#10b981" /> : <Copy size={14} color="#64748b" />}
                        </button>
                      </div>
                    </td>
                    <td>
                      {p.mrp && p.mrp > 0 ? (
                        <span style={{ color: '#94a3b8', fontWeight: '600', fontSize: '13px', textDecoration: 'line-through' }}>
                          ₹{Number(p.mrp).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      ) : (
                        <span style={{ color: '#94a3b8', fontWeight: '600' }}>—</span>
                      )}
                    </td>
                    <td>
                      <strong style={{ color: '#059669', fontWeight: '700', background: '#d1fae5', padding: '4px 8px', borderRadius: '6px', fontSize: '13px' }}>
                        ₹{Number(p.price || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </strong>
                    </td>
                    <td>
                      {p.discount > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                          <span style={{ color: '#ea580c', fontWeight: '700', background: '#ffedd5', padding: '4px 8px', borderRadius: '6px', fontSize: '12px', display: 'flex', alignItems: 'center', boxShadow: '0 1px 2px rgba(234, 88, 12, 0.1)' }}>
                            {Number(p.discount).toFixed(1)}% OFF
                          </span>
                          <span style={{ color: '#059669', fontSize: '11px', fontWeight: '600', background: '#d1fae5', padding: '2px 6px', borderRadius: '4px' }}>
                            Save ₹{Math.round((p.mrp || p.price || 0) - (p.price || 0)).toLocaleString('en-IN')}
                          </span>
                        </div>
                      ) : (
                        <span style={{ color: '#94a3b8', fontWeight: '600' }}>—</span>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontWeight: '700', fontSize: '14px', color: p.stock <= p.lowStockAlert ? '#ef4444' : '#0f172a' }}>
                          {p.stock} <span style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>units</span>
                        </span>
                        {p.stock <= p.lowStockAlert && <span style={{ fontSize: '11px', color: '#ef4444', fontWeight: '600', marginTop: '2px' }}>Low stock alert!</span>}
                      </div>
                    </td>
                    <td>
                      <span style={{
                        padding: '4px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', display: 'inline-block',
                        background: (p.status === 'A' || p.status?.toLowerCase() === 'active') ? '#dcfce7' :
                          (p.status === 'D' || p.status?.toLowerCase() === 'draft') ? '#fef9c3' : '#fee2e2',
                        color: (p.status === 'A' || p.status?.toLowerCase() === 'active') ? '#166534' :
                          (p.status === 'D' || p.status?.toLowerCase() === 'draft') ? '#854d0e' : '#991b1b'
                      }}>
                        {(p.status === 'A' || p.status?.toLowerCase() === 'active') ? 'Active' :
                          (p.status === 'D' || p.status?.toLowerCase() === 'draft') ? 'Draft' :
                            (p.status === 'O' || p.status?.toLowerCase() === 'out_of_stock') ? 'Out of Stock' : p.status}
                      </span>
                    </td>
                    <td style={{ paddingRight: '24px' }}>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {/* <button onClick={() => navigate(`/products/view/${p._id}`)}
                          style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', transition: 'all 0.2s', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }} 
                          onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.background = '#f8fafc'; }} onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.background = 'white'; }}
                          title="View Details">
                          <Eye size={16} color="#475569" />
                        </button> */}
                        <button onClick={() => navigate(`/products/edit/${p._id}`, { state: { product: p } })}
                          style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', transition: 'all 0.2s' }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#dbeafe'; }} onMouseLeave={(e) => { e.currentTarget.style.background = '#eff6ff'; }}
                          title="Edit">
                          <Edit2 size={16} color="#2563eb" />
                        </button>
                        <button onClick={() => handleDelete(p._id)} disabled={deleting === p._id}
                          style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', transition: 'all 0.2s', opacity: deleting === p._id ? 0.5 : 1 }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#fee2e2'; }} onMouseLeave={(e) => { e.currentTarget.style.background = '#fef2f2'; }}
                          title="Delete">
                          <Trash2 size={16} color="#dc2626" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Card Footer: Pagination */}
        {!loading && products.length > 0 && (
          <div className="products-card-footer">
            <div style={{ fontSize: '13px', color: '#64748b', fontWeight: '500' }}>
              Showing <span style={{ fontWeight: '700', color: '#0f172a' }}>{(pagination.page - 1) * (pagination.limit || 10) + 1}</span> to <span style={{ fontWeight: '700', color: '#0f172a' }}>{Math.min(pagination.page * (pagination.limit || 10), pagination.total)}</span> of <span style={{ fontWeight: '700', color: '#0f172a' }}>{pagination.total}</span> products
            </div>

            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button
                disabled={Number(pagination.page) <= 1}
                onClick={() => fetchProducts({ search, status, page: Number(pagination.page) - 1, sortBy, sortOrder })}
                style={{
                  padding: '8px', borderRadius: '8px', border: '1px solid #e2e8f0', background: 'white',
                  cursor: Number(pagination.page) <= 1 ? 'not-allowed' : 'pointer', opacity: Number(pagination.page) <= 1 ? 0.5 : 1,
                  display: 'flex', alignItems: 'center', color: '#475569', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => { if (Number(pagination.page) > 1) e.currentTarget.style.background = '#f8fafc'; }}
                onMouseLeave={(e) => { if (Number(pagination.page) > 1) e.currentTarget.style.background = 'white'; }}
                title="Previous page"
              >
                <ChevronLeft size={16} />
              </button>

              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                {getPaginationGroup().map((pg, i) => {
                  if (pg === "...") {
                    return (
                      <span
                        key={`ellipsis-${i}`}
                        style={{
                          width: '30px',
                          height: '34px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#94a3b8',
                          fontWeight: '700',
                          fontSize: '14px',
                          userSelect: 'none'
                        }}
                      >
                        …
                      </span>
                    );
                  }

                  const pageNum = Number(pg);
                  const isActive = Number(pagination.page) === pageNum;

                  return (
                    <button
                      key={pageNum}
                      onClick={() => fetchProducts({ search, status, page: pageNum, sortBy, sortOrder })}
                      style={{
                        width: '34px',
                        height: '34px',
                        borderRadius: '8px',
                        border: '1px solid',
                        borderColor: isActive ? '#14b8a6' : '#e2e8f0',
                        background: isActive ? '#14b8a6' : 'white',
                        color: isActive ? 'white' : '#475569',
                        fontWeight: isActive ? '700' : '600',
                        cursor: 'pointer',
                        fontSize: '13px',
                        transition: 'all 0.2s',
                        boxShadow: isActive ? '0 4px 12px rgba(20,184,166,0.2)' : '0 1px 2px rgba(0,0,0,0.05)'
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.borderColor = '#cbd5e1';
                          e.currentTarget.style.background = '#f8fafc';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.borderColor = '#e2e8f0';
                          e.currentTarget.style.background = 'white';
                        }
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                disabled={Number(pagination.page) >= (Number(pagination.pages) || 1)}
                onClick={() => fetchProducts({ search, status, page: Number(pagination.page) + 1, sortBy, sortOrder })}
                style={{
                  padding: '8px', borderRadius: '8px', border: '1px solid #e2e8f0', background: 'white',
                  cursor: Number(pagination.page) >= (Number(pagination.pages) || 1) ? 'not-allowed' : 'pointer',
                  opacity: Number(pagination.page) >= (Number(pagination.pages) || 1) ? 0.5 : 1,
                  display: 'flex', alignItems: 'center', color: '#475569', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => { if (Number(pagination.page) < (Number(pagination.pages) || 1)) e.currentTarget.style.background = '#f8fafc'; }}
                onMouseLeave={(e) => { if (Number(pagination.page) < (Number(pagination.pages) || 1)) e.currentTarget.style.background = 'white'; }}
                title="Next page"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
